import React, { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { Navigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { AlertTriangle } from 'lucide-react'
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport'
import { useTranslation } from "react-i18next";

interface ProtectedRouteProps {
  children: React.ReactNode
}

function isLicenseExpired(expireDate: string | null | undefined): boolean {
  if (!expireDate) return false
  const today = new Date().toISOString().slice(0, 10)
  return expireDate < today
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { t } = useTranslation("common");
  const { user, isAuthenticated, loading } = useAuth()
  const isMobile = useIsMobileViewport()
  const [initialCheckDone, setInitialCheckDone] = useState(false)
  const [accountDisabled, setAccountDisabled] = useState(false)

  const checkAccountStatus = useCallback(async () => {
    if (!isAuthenticated || !user?.account_id || !supabase) {
      setAccountDisabled(false)
      setInitialCheckDone(true)
      return
    }

    const { data, error } = await supabase
      .from('accounts')
      .select('is_active, licenses(expire_date)')
      .eq('id', user.account_id)
      .maybeSingle()

    if (error) {
      console.error('[ProtectedRoute] Erro ao verificar status da conta/licença:', error)
      // Fail closed: bloqueia acesso se não conseguir validar
      setAccountDisabled(true)
      setInitialCheckDone(true)
      return
    }

    const license = Array.isArray(data?.licenses) ? data.licenses[0] : data?.licenses
    const licenseExpired = isLicenseExpired(
      (license as { expire_date?: string } | null | undefined)?.expire_date
    )

    setAccountDisabled(data?.is_active === false || licenseExpired)
    setInitialCheckDone(true)
  }, [isAuthenticated, user?.account_id])

  useEffect(() => {
    if (!isAuthenticated || !user?.account_id) {
      setAccountDisabled(false)
      setInitialCheckDone(true)
      return
    }

    setInitialCheckDone(false)
    void checkAccountStatus()

    const handleFocus = () => {
      void checkAccountStatus()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkAccountStatus()
      }
    }

    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [checkAccountStatus, isAuthenticated, user?.account_id])

  if (loading || (isAuthenticated && !initialCheckDone)) {
    return (
      <div className="w-full h-dvh flex items-center justify-center bg-gray-400">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-neutral-600 border-t-neutral-50 rounded-full animate-spin"></div>
          <p className="text-neutral-50 text-lg">{t("shared.checkingSession")}</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to={isMobile ? '/m/login' : '/login'} replace />
  }

  if (accountDisabled) {
    return (
      <div className="w-full h-dvh flex items-center justify-center bg-[#18181B] px-4">
        <div className="w-full max-w-lg rounded-2xl border border-red-500/30 bg-[#27272A] p-8 text-center shadow-2xl">
          <div className="mx-auto mb-5 flex size-14 items-center justify-center rounded-full bg-red-500/10">
            <AlertTriangle className="size-7 text-red-400" aria-hidden="true" />
          </div>
          <h1 className="text-xl font-semibold text-white">
            {t("shared.contactAdmin")}
          </h1>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
